import React, { useCallback, useLayoutEffect } from 'react';
import Animated from 'react-native-reanimated';
import { AppView, Button, ButtonGroup, CardView, rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import useColors from 'hooks/useColors';
import { useUserAddress } from 'hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import FormattedAddress from 'features/invest/components/FormattedAddress';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { selectSecondaryAddresses } from 'reducers/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { OnboardingFlow } from 'utils/types/navigation';
import { updateUserAddress } from 'actions';
import { format } from 'date-fns';
import useCreditScoreRegistration from '../hooks/useCreditScoreRegistration';
import { needsPreviousAddress } from '../utils';
const strings = {
    title: 'Review your address',
    subTitle: `We’ll use your current address to find your credit report. If your address is not up to date, we might not find your credit report so please update it first`,
    changeAddress: 'Change address',
    continueButton: 'Continue',
};
const CreditScoreReviewKYCAddress = withAnimatedNavigationHeader(({ navigation, scrollHandler }) => {
    const colors = useColors();
    const { primaryAddress } = useUserAddress();
    const secondaryAddresses = useAppSelector(selectSecondaryAddresses);
    const { register } = useCreditScoreRegistration(true);
    const contentContainerStyle = useMarginBottom('paddingBottom', 2 * HEIGHT + SCREEN_BOTTOM_PADDING + rem(16));
    const dispatch = useAppDispatch();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const onPressChangeAddress = useCallback(() => {
        navigation.navigate('ChangeAddressProgressScreen', {
            flow: 'CHANGE',
        });
    }, []);
    const handleContinue = useCallback((address) => {
        const hasPreviousWithDates = !!secondaryAddresses.find((a) => a.endDate);
        if (!needsPreviousAddress(address) || hasPreviousWithDates) {
            register(address.id);
            return;
        }
        if (secondaryAddresses.length) {
            navigation.navigate('ChoosePreviousAddressScreen', {
                flow: OnboardingFlow.CreditScore,
            });
            return;
        }
        navigation.navigate('AddressPostCodeScreen', {
            flow: OnboardingFlow.CreditScore,
            isAddingSecondaryAddress: true,
        });
    }, [register, secondaryAddresses]);
    const onPressContinue = useCallback(() => {
        if (!primaryAddress?.id)
            return;
        if (!primaryAddress.startDate) {
            navigation.navigate('AddressDateScreen', {
                mode: 'moveIn',
                startDate: primaryAddress.startDate,
                endDate: primaryAddress.endDate,
                onSave: (moveInDate) => {
                    const startDate = format(moveInDate, 'yyyy-MM-dd');
                    dispatch(updateUserAddress(primaryAddress.id, { startDate }));
                    handleContinue({
                        ...primaryAddress,
                        startDate: moveInDate.toISOString(),
                    });
                },
            });
            return;
        }
        handleContinue(primaryAddress);
    }, [handleContinue, primaryAddress]);
    if (!primaryAddress)
        return null;
    return (<AppView>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
            <ScreenInfoHeader title={strings.title} subTitle={strings.subTitle}/>
            <CardView noMarginTop noMarginBottom withPadding>
              <FormattedAddress {...primaryAddress} uk/>
            </CardView>
          </Animated.ScrollView>
          <ButtonGroup type="floating">
            <Button brandReversed title={strings.changeAddress} onPress={onPressChangeAddress}/>
            <Button brand title={strings.continueButton} onPress={onPressContinue}/>
          </ButtonGroup>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export default CreditScoreReviewKYCAddress;
